import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, MessageSquare, MessageCircle, TrendingUp, Briefcase, FileText, Settings, LogOut, ChevronRight, User, Brain, Target, Shield, Clock, Compass } from 'lucide-react';
import { logout, getUser } from '../utils/auth';
import MobileBottomNav from './MobileBottomNav';

const Layout = ({ children, fullBleed = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const user = getUser();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigation = [
    {
      name: 'LEE',
      path: '/chat',
      icon: MessageCircle,
      description: 'One LEE — chat, voice, files',
      highlight: true
    },
    {
      name: 'Scheduled',
      path: '/scheduled',
      icon: Clock,
      description: 'LEE runs tasks for you'
    },
    { 
      name: 'Command Center', 
      path: '/dashboard', 
      icon: LayoutDashboard,
      description: 'Overview'
    },
    { 
      name: 'AI Command', 
      path: '/ai-command', 
      icon: MessageSquare,
      description: 'AI Command'
    },
    { 
      name: 'Progress', 
      path: '/progress', 
      icon: TrendingUp,
      description: 'Tasks'
    },
    { 
      name: 'Activity', 
      path: '/activity', 
      icon: Brain,
      description: 'Timeline'
    },
    { 
      name: 'Wealth', 
      path: '/wealth', 
      icon: TrendingUp,
      description: 'Portfolio'
    },
    { 
      name: 'Credit', 
      path: '/credit', 
      icon: Shield,
      description: 'Credit Intel'
    },
    { 
      name: 'Opportunities', 
      path: '/opportunities', 
      icon: Target,
      description: 'Wealth Multiplier'
    },
    { 
      name: 'Intelligence', 
      path: '/intelligence', 
      icon: Brain,
      description: 'Adaptive AI'
    },
    { 
      name: 'Initiatives', 
      path: '/initiatives', 
      icon: Target,
      description: 'Execution'
    },
    { 
      name: 'Inventions', 
      path: '/inventions', 
      icon: Briefcase,
      description: 'Ideas to Production'
    },
    { 
      name: 'Scout', 
      path: '/scout', 
      icon: Compass,
      description: 'Candidates & Costs'
    },
    { 
      name: 'Documents', 
      path: '/documents', 
      icon: FileText,
      description: 'Files'
    },
  ];

  return (
    <div className="min-h-screen bg-[#030303] relative">
      {/* Desktop Sidebar - Hidden on Mobile */}
      <div 
        className={`hidden md:block fixed left-0 top-0 h-full z-50 transition-all duration-300 ease-in-out ${
          isExpanded ? 'w-64' : 'w-20'
        }`}
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
        style={{
          background: 'rgba(10, 10, 11, 0.6)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderRight: '1px solid rgba(255, 255, 255, 0.08)'
        }}
      >
        {/* Logo */}
        <div className="p-5 border-b border-white/[0.06]">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-center justify-center">
              <span className="text-amber-500 font-bold text-sm font-mono">L</span>
            </div>
            {isExpanded && (
              <div className="overflow-hidden transition-all duration-200">
                <h1 className="text-base font-bold tracking-tight font-mono text-white whitespace-nowrap" data-testid="logo">LEE</h1>
              </div>
            )}
          </div>
        </div>

        {/* Navigation - Clean Icons */}
        <nav className="flex-1 py-4 space-y-0.5 overflow-y-auto scrollbar-hide" data-testid="main-navigation">
          {navigation.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.replace('/', '')}`}
                className={`flex items-center gap-3.5 px-5 py-2.5 mx-2 rounded-lg transition-all duration-200 relative group ${
                  isActive
                    ? 'text-amber-500 bg-amber-500/[0.08]'
                    : 'text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.03]'
                }`}
                title={item.name}
              >
                <div className="relative flex-shrink-0">
                  <Icon className="w-[18px] h-[18px]" />
                  {item.highlight && !isActive && (
                    <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-amber-500 rounded-full"></div>
                  )}
                </div>

                {isExpanded && (
                  <div className="overflow-hidden transition-all duration-200">
                    <span className="text-[13px] font-medium whitespace-nowrap block">
                      {item.name}
                    </span>
                  </div>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Bottom Actions */}
        <div className="p-4 border-t border-white/10 space-y-1">
          {/* User Info */}
          {user && isExpanded && (
            <div className="px-6 py-3 mb-2">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-primary" />
                </div>
                <div className="overflow-hidden">
                  <p className="text-sm font-semibold text-white truncate">{user.full_name || user.email}</p>
                  <p className="text-xs text-green-500 uppercase tracking-wider">● Logged In</p>
                </div>
              </div>
            </div>
          )}
          
          <button
            onClick={() => navigate('/settings')}
            className="flex items-center gap-4 px-6 py-3 w-full text-muted-foreground hover:text-white transition-colors"
            data-testid="settings-button"
            title="Settings"
          >
            <Settings className="w-5 h-5 flex-shrink-0" />
            {isExpanded && (
              <span className="text-sm font-medium whitespace-nowrap">Settings</span>
            )}
          </button>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="flex items-center gap-4 px-6 py-3 w-full text-muted-foreground hover:text-red-400 transition-colors"
            title="Logout"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {isExpanded && (
              <span className="text-sm font-medium whitespace-nowrap">Logout</span>
            )}
          </button>
        </div>

        {/* Expand Indicator */}
        {!isExpanded && (
          <div className="absolute top-1/2 -right-3 transform -translate-y-1/2 w-6 h-12 bg-primary/20 rounded-r-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <ChevronRight className="w-4 h-4 text-primary" />
          </div>
        )}
      </div>

      {/* Main Content - Adaptive Spacing */}
      <div className={`transition-all duration-300 ease-in-out md:ml-20 md:${isExpanded ? 'ml-64' : 'ml-20'} min-h-screen ${fullBleed ? '' : 'pb-20 md:pb-0'}`}>
        {fullBleed ? (
          // U2: full-bleed for surfaces (like TranscendentChat) that own their own layout.
          children
        ) : (
          /* Content Wrapper with Max Width & Generous Padding */
          <div className="max-w-[1600px] mx-auto px-4 py-6 md:px-8 md:py-10 lg:px-12 lg:py-12">
            {children}
          </div>
        )}
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
};

export default Layout;
